import type { VatechCtCatalogStudy } from "./vatechCtCatalog"

export type DiagnocatStudyRef = {
  study_uid: string
  study_date?: string | null
  analyses?: Array<{ analysis_uid: string; status?: string | null }>
}

function normalizeStudyDate(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed) return null
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10)
  const dmy = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
  return trimmed.slice(0, 10)
}

function matchByFolderOrName(
  study: VatechCtCatalogStudy,
  dc: DiagnocatStudyRef,
): boolean {
  const folder = String(study.folder_name ?? study.raw_folder_name ?? study.patient_id ?? "").trim()
  const dcName = String((dc as { study_name?: string | null }).study_name ?? "").trim()
  return Boolean(folder && dcName && folder === dcName)
}

function countReadyAnalyses(analyses: DiagnocatStudyRef["analyses"]): number {
  if (!analyses?.length) return 0
  return analyses.filter((analysis) => {
    const status = String(analysis.status ?? "").trim().toLowerCase()
    if (!status) return true
    return status === "completed" || status === "ready" || status === "done"
  }).length
}

export function enrichVatechStudiesWithDiagnocat(
  studies: VatechCtCatalogStudy[],
  diagnocatStudies: DiagnocatStudyRef[] | undefined,
): VatechCtCatalogStudy[] {
  if (!diagnocatStudies?.length) return studies

  const analysisDates = new Set(
    diagnocatStudies
      .filter((s) => (s.analyses?.length ?? 0) > 0)
      .map((s) => normalizeStudyDate(s.study_date))
      .filter(Boolean) as string[]
  )

  const byUid = new Map<string, DiagnocatStudyRef>()
  const byDate = new Map<string, DiagnocatStudyRef>()
  for (const study of diagnocatStudies) {
    if (study.study_uid) byUid.set(study.study_uid, study)
    const date = normalizeStudyDate(study.study_date)
    if (date && !byDate.has(date)) byDate.set(date, study)
  }

  return studies.map((study) => {
    const dateKey = normalizeStudyDate(study.study_date)
    const match =
      (study.study_uid ? byUid.get(study.study_uid) : undefined) ??
      (dateKey ? byDate.get(dateKey) : undefined) ??
      diagnocatStudies.find((dc) => matchByFolderOrName(study, dc))
    if (!match) {
      const dateKeyOnly = normalizeStudyDate(study.study_date)
      if (dateKeyOnly && analysisDates.has(dateKeyOnly)) {
        return {
          ...study,
          already_in_diagnocat: true,
          diagnocat_analysis_ready: true,
          diagnocat_analyses_count: Math.max(study.diagnocat_analyses_count ?? 0, 1),
        }
      }
      return study
    }

    const analyses = match.analyses ?? []
    const readyCount = countReadyAnalyses(analyses)
    const totalCount = analyses.length

    return {
      ...study,
      already_in_diagnocat: study.already_in_diagnocat || Boolean(match.study_uid) || totalCount > 0,
      diagnocat_analyses_count: Math.max(study.diagnocat_analyses_count ?? 0, totalCount),
      diagnocat_analysis_ready: Boolean(study.diagnocat_analysis_ready) || totalCount > 0,
      diagnocat_analyses_ready_count: Math.max(study.diagnocat_analyses_ready_count ?? 0, readyCount > 0 ? readyCount : totalCount),
    }
  })
}

export function pickStudyWithDiagnocatAnalysis(studies: VatechCtCatalogStudy[]): VatechCtCatalogStudy | null {
  const withAnalysis = studies.filter((s) => s.diagnocat_analysis_ready)
  if (withAnalysis.length === 0) return null
  return [...withAnalysis].sort((a, b) => (b.study_date ?? "").localeCompare(a.study_date ?? ""))[0]
}
